import { useEffect, useMemo, useRef, useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import { CheckCircle2, Loader2 } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateSetupIntent } from '@/api/portalPayments';

// Phase 8 of docs/build-plans/pma-stripe-payments.md.
//
// "Save a card without paying" dialog. Mints a SetupIntent on mount,
// wraps Stripe Elements, lets the parent enter card details + confirm.
// On success the payment_method.attached webhook handler upserts a
// parent_payment_methods row + auto-promotes the first one to default.
// Closing the dialog invalidates the saved-methods query so the list
// updates without a manual refresh.
//
// Mirrors InvoicePaymentForm's lifecycle (useState + useRef guard for
// React 19 StrictMode, per the comment over there). The two could
// share a Stripe-promise cache + sub-form helper if they grow more
// shared shape, but for v1 the duplication is small enough.

const stripePromises = new Map<string, Promise<Stripe | null>>();
function getStripePromise(publishableKey: string, stripeAccount: string): Promise<Stripe | null> {
    const cacheKey = `${publishableKey}::${stripeAccount}`;
    let p = stripePromises.get(cacheKey);
    if (!p) {
        p = loadStripe(publishableKey, { stripeAccount });
        stripePromises.set(cacheKey, p);
    }
    return p;
}

interface IntentBundle {
    clientSecret: string;
    publishableKey: string;
    stripeAccount: string;
    businessName: string | null;
}

export function AddCardDialog({ onClose }: { onClose: () => void }) {
    const setupIntent = useCreateSetupIntent();
    const [data, setData] = useState<IntentBundle | null>(null);
    const [error, setError] = useState<string | null>(null);
    const fired = useRef(false);
    // Autopay-consent split: parent opts in or out before the
    // SetupIntent is minted. Toggling re-mints because Stripe doesn't
    // support updating a SetupIntent's `usage` post-creation. Re-mint
    // is acceptable here — AddCardDialog is small and the toggle is
    // a one-time choice per save attempt.
    const [autopayConsent, setAutopayConsent] = useState(false);
    const [busy, setBusy] = useState(false);

    function mintSetupIntent(forAutopay: boolean) {
        setData(null);
        setError(null);
        setBusy(true);
        setupIntent.mutate(forAutopay, {
            onSuccess: (res) => {
                setData({
                    clientSecret:   res.clientSecret,
                    publishableKey: res.publishableKey,
                    stripeAccount:  res.stripeAccount,
                    businessName:   res.businessName,
                });
                setBusy(false);
            },
            onError: (err) => {
                setError(err instanceof Error ? err.message : 'Could not start card save.');
                setBusy(false);
            },
        });
    }

    useEffect(() => {
        if (fired.current) return;
        fired.current = true;
        mintSetupIntent(false);    // default: save-only (on_session)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function toggleAutopay(next: boolean) {
        if (busy) return;
        setAutopayConsent(next);
        mintSetupIntent(next);
    }

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="w-[min(calc(100vw-2rem),32rem)]">
                <DialogHeader>
                    <DialogTitle>Save a payment method</DialogTitle>
                    <DialogDescription>
                        Card details are sent directly to Stripe — they never touch this site.
                    </DialogDescription>
                </DialogHeader>
                {/*
                  Autopay-consent split. Default off — opting in
                  surfaces Stripe's mandate text inside the form
                  (off_session usage). Toggling re-mints the
                  SetupIntent because Stripe doesn't support updating
                  usage post-create.
                */}
                <label className="flex items-start gap-2 text-sm text-text-muted mb-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={autopayConsent}
                        onChange={(e) => toggleAutopay(e.target.checked)}
                        disabled={busy}
                        className="mt-0.5"
                    />
                    <span>
                        Allow this card to be used for autopay on future invoices
                    </span>
                </label>
                {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                {!data && !error ? (
                    <div className="py-6 flex items-center justify-center text-text-subtle text-sm">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Preparing secure form…
                    </div>
                ) : data ? (
                    <ElementsHost {...data} onSuccess={onClose} onCancel={onClose} />
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

function ElementsHost({
    clientSecret, publishableKey, stripeAccount, businessName, onSuccess, onCancel,
}: IntentBundle & { onSuccess: () => void; onCancel: () => void }) {
    const stripePromise = useMemo(
        () => getStripePromise(publishableKey, stripeAccount),
        [publishableKey, stripeAccount],
    );
    return (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
            <Inner businessName={businessName} onSuccess={onSuccess} onCancel={onCancel} />
        </Elements>
    );
}

function Inner({
    businessName, onSuccess, onCancel,
}: { businessName: string | null; onSuccess: () => void; onCancel: () => void }) {
    const stripe = useStripe();
    const elements = useElements();
    const qc = useQueryClient();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!stripe || !elements || submitting) return;
        setSubmitting(true);
        setError(null);
        const result = await stripe.confirmSetup({
            elements,
            redirect: 'if_required',
        });
        if (result.error) {
            setSubmitting(false);
            const type = result.error.type;
            const safe = type === 'card_error' || type === 'validation_error';
            setError(safe && result.error.message
                ? result.error.message
                : 'Could not save card. Please try a different one.');
            return;
        }
        setSubmitting(false);
        setDone(true);
        // Webhook payment_method.attached lands ~1s later and inserts
        // the parent_payment_methods row. Invalidate immediately so a
        // refetch picks it up; the section re-renders the new card
        // when the next /api/portal/payment-methods response arrives.
        qc.invalidateQueries({ queryKey: ['portal', 'payment-methods'] });
        // Brief pause so the parent sees the success state before the
        // dialog closes and the list re-renders with their new card.
        setTimeout(onSuccess, 800);
    }

    if (done) {
        return (
            <div className="py-6 px-4 bg-success-soft border border-success-border rounded-md flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-success-text shrink-0 mt-0.5" />
                <div className="text-sm">
                    <div className="font-medium text-success-text">Card saved.</div>
                    <div className="text-text-muted mt-1">
                        It'll show up in your saved methods in a moment.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <PaymentElement
                options={{
                    // Mandate-text override + defensive Link suppression.
                    // Mirrors the InvoicePaymentForm options for the
                    // same reasons (test-mode placeholder, two-prompt
                    // confusion).
                    ...(businessName ? { business: { name: businessName } } : {}),
                    wallets: { link: 'never', applePay: 'auto', googlePay: 'auto' },
                } as React.ComponentProps<typeof PaymentElement>['options']}
            />
            {error && <Alert variant="danger">{error}</Alert>}
            <DialogFooter>
                <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={!stripe || !elements || submitting}>
                    {submitting ? 'Saving…' : 'Save card'}
                </Button>
            </DialogFooter>
        </form>
    );
}
